import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  ActiveFilterViewer,
  AreasSelector,
  Filters,
} from '@rankmi/rankmi-table'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { Col, Row, Tab, Tabs } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath, useLocation, useHistory, Switch, Route } from 'react-router'
import { IAreaModel } from 'models/area.model'
import * as fromAreaActions from 'store/actions/areas.action'
import { IMainState } from 'store/states'
import { PathRoutes } from 'enum/routes'
import { ChildFilterTypes } from 'enum'
import { useTranslationRankmi } from '../../helpers/translations.helper'
import { FiltersColumns, prefix } from './constants'
import { useFiltersColumns } from './hooks'
import styles from './monitoring.module.scss'
import { Areas, Courses, ExternalCourses, LearningPaths } from './Tabs'
import { DownloadReport } from './DownloadReport'
import { AreaProgress } from './components/AreaProgress'
import { Course } from './components'
import ExternalCourse from './components/ExternalCourse'
import { Participants } from './Tabs/Participants'
import { transformFilterData } from './components/CoursesTab/utils'
import { ApplyFiltersByType } from './types'
import { useMonitoring } from './context'
import { useHasLearningPaths } from './hooks/useHasLearningPaths'
import { GlobalProgress } from './components/GlobalProgress'
import Wrapper from 'components/reusables/wrapper'

export const Monitoring = () => {
  const { areas, loading: areasLoading } = useSelector(
    ({ areas }: IMainState) => areas
  )

  const hasLearningPaths = useHasLearningPaths()
  const tRelative = useTranslationRankmi({ prefix })
  const dispatch = useDispatch()
  const { clearSelectedItems } = useMonitoring()
  const [area, setArea] = useState<IAreaModel>()

  const allAreas = useMemo(() => !!(area && +area.id === 0), [area])

  const { pathname } = useLocation()
  const history = useHistory()
  const activeKey = useMemo(() => {
    const tabs = [
      { path: PathRoutes.MONITORING_COURSES },
      { path: PathRoutes.MONITORING_PARTICIPANTS },
      { path: PathRoutes.MONITORING_EXTERNAL_COURSES },
      { path: PathRoutes.MONITORING_LEARNING_PATHS },
      { path: PathRoutes.MONITORING_AREAS, exact: true },
    ]
    return tabs.find(({ path, exact }) =>
      matchPath(pathname, {
        path,
        exact,
      })
    )
  }, [pathname])

  const [showFiltersModal, setShowFiltersModal] = useState(false)
  useEffect(() => {
    dispatch({ type: fromAreaActions.GET_AREAS_LIST })
  }, [dispatch])

  const filtersColumns = useFiltersColumns()

  const defaultFilterData = useMemo(
    () =>
      filtersColumns.find((column) => column.key === FiltersColumns.userState),
    [filtersColumns]
  )

  const [filterData, setFilterData] = useState<IFilterData | undefined>()

  useEffect(() => {
    if (defaultFilterData)
      setFilterData({
        data: [
          {
            group: 0,
            input: defaultFilterData.value as any,
            selectedValueData: defaultFilterData,
            type: 'tagsList',
            operator: 'eq',
            compareTo: 'true',
            index: 0,
            condition: 'and',
          },
        ],
        generalCondition: 'and',
        filterType: 'simple',
      })
  }, [defaultFilterData])

  const applyFiltersByType: ApplyFiltersByType = useCallback(
    (type: ChildFilterTypes, maps?: Record<string, string>) => {
      return transformFilterData<ChildFilterTypes>(filterData, {
        type,
        maps: maps,
      })
    },
    [filterData]
  )

  const areaFilters = useMemo(
    () => applyFiltersByType(ChildFilterTypes.Area).filters,
    [applyFiltersByType]
  )

  const areaId = area?.id
  return (
    <Switch>
      <Route exact path={PathRoutes.MONITORING_COURSE}>
        <Course />
      </Route>
      <Route path={PathRoutes.MONITORING_EXTERNAL_COURSE}>
        <ExternalCourse />
      </Route>
      <Route path={PathRoutes.MONITORING}>
        <Wrapper shadow={true}>
          <span className={styles.title}>{tRelative('title')}</span>
          <Row>
            <Col xs={12}>
              <Row noGutters>
                <Col
                  xs={4}
                  className={classNames(
                    'd-flex flex-column justify-content-center',
                    styles.areasSelector
                  )}
                >
                  <AreasSelector
                    areas={areas}
                    loading={areasLoading}
                    selectedArea={area}
                    mandatoryArea={false}
                    onAreaSelected={(newArea: IAreaModel) => {
                      setArea(newArea)
                    }}
                  />
                </Col>
                <Col>
                  <div className="d-flex align-items-center">
                    <Filters
                      columns={filtersColumns}
                      onChange={(value) => setFilterData(value)}
                      open={showFiltersModal}
                      onVisibilityChange={(state: boolean) =>
                        setShowFiltersModal(state)
                      }
                      selectedFilters={filterData}
                    />
                    <DownloadReport
                      filters={areaFilters}
                      area={allAreas ? null : area}
                    />
                  </div>
                </Col>
                {filterData && (
                  <Col xs={12}>
                    <ActiveFilterViewer
                      activeFilters={filterData}
                      onChange={(data: any) => setFilterData(data)}
                      openFilters={(state: boolean) =>
                        setShowFiltersModal(state)
                      }
                      columns={filtersColumns}
                    />
                  </Col>
                )}
              </Row>
            </Col>
            <Col xs={12} className="mb-2">
              <div className={styles.titleGlobal}>{tRelative('progress')}</div>
            </Col>
            <Col xs={12}>
              <Row>
                {!!areaId && (
                  <AreaProgress
                    areaId={areaId}
                    applyFiltersByType={applyFiltersByType}
                  />
                )}
                {!areaId && (
                  <GlobalProgress applyFiltersByType={applyFiltersByType} />
                )}
              </Row>
            </Col>
            <Col xs={12}>
              <Tabs
                className={classNames(styles.tabsMenu, 'mt-2 mb-2 mx-0')}
                activeKey={activeKey?.path}
                onSelect={(route) => {
                  clearSelectedItems()
                  route && history.push(route)
                }}
                unmountOnExit
              >
                {hasLearningPaths && (
                  <Tab
                    eventKey={PathRoutes.MONITORING_LEARNING_PATHS}
                    title={tRelative('learningPaths.title')}
                  >
                    <LearningPaths />
                  </Tab>
                )}
                <Tab
                  eventKey={PathRoutes.MONITORING_AREAS}
                  title={tRelative('areas.title')}
                >
                  <Areas
                    area={allAreas ? null : area}
                    applyFiltersByType={applyFiltersByType}
                  />
                </Tab>
                <Tab
                  eventKey={PathRoutes.MONITORING_COURSES}
                  title={tRelative('courses.title')}
                >
                  <Courses
                    area={allAreas ? null : area}
                    applyFiltersByType={applyFiltersByType}
                  />
                </Tab>
                <Tab
                  eventKey={PathRoutes.MONITORING_EXTERNAL_COURSES}
                  title={tRelative('externalCourses.title')}
                >
                  <ExternalCourses
                    area={allAreas ? null : area}
                    applyFiltersByType={applyFiltersByType}
                  />
                </Tab>
                <Tab
                  eventKey={PathRoutes.MONITORING_PARTICIPANTS}
                  title={tRelative('participants.title')}
                >
                  <Participants
                    area={allAreas ? null : area}
                    applyFiltersByType={applyFiltersByType}
                  />
                </Tab>
                <Tab title="" tabClassName="flex-fill" disabled />
              </Tabs>
            </Col>
          </Row>
        </Wrapper>
      </Route>
    </Switch>
  )
}
